import React from 'react';
import { makeStyles } from 'tss-react/mui';
import Dialog from '../../../base/ui/components/web/Dialog';
import { useCaptionTranscript } from '../../useCaptionTranscript';
const useStyles = makeStyles()(theme => ({
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'flex-end',
        marginBottom: 12
    },
    button: {
        background: theme.palette.action01,
        border: 0,
        borderRadius: 4,
        color: theme.palette.text01,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 700,
        lineHeight: '20px',
        minHeight: 44,
        padding: '8px 12px',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 },
        '&:disabled': { cursor: 'default', opacity: 0.5 }
    },
    exportError: {
        color: theme.palette.text01,
        fontSize: 14,
        lineHeight: '21px',
        marginBottom: 12
    },
    empty: {
        color: theme.palette.text02,
        fontSize: 14,
        padding: '24px 0'
    },
    privacyNote: {
        background: theme.palette.ui02,
        border: `1px solid ${theme.palette.ui04}`,
        borderRadius: 6,
        color: theme.palette.text02,
        fontSize: 13,
        lineHeight: '18px',
        marginBottom: 12,
        padding: '10px 12px'
    },
    entry: {
        borderBottom: `1px solid ${theme.palette.ui04}`,
        padding: '10px 0'
    },
    meta: {
        color: theme.palette.text02,
        display: 'flex',
        flexWrap: 'wrap',
        fontSize: 13,
        gap: 8,
        lineHeight: '19px',
        overflowWrap: 'anywhere',
        marginBottom: 4
    },
    text: {
        color: theme.palette.text01,
        fontSize: 16,
        lineHeight: '24px',
        overflowWrap: 'anywhere',
        textAlign: 'start',
        whiteSpace: 'pre-wrap'
    },
    transcript: {
        maxHeight: 420,
        overflowY: 'auto'
    }
}));
const CaptionTranscriptDialog = () => {
    const { classes } = useStyles();
    const { canExport, entries, exportStatus, exportTranscript, preferences, toneLabel, transcriptRetentionAllowed, t, formatTime } = useCaptionTranscript();
    const copyTranscript = () => exportTranscript(async (text) => {
        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            throw new Error('Clipboard unavailable');
        }
        await navigator.clipboard.writeText(text);
        return true;
    });
    return (React.createElement(Dialog, { cancel: { hidden: true }, ok: { hidden: true }, size: 'medium', title: t('captionTranscript.title') },
        React.createElement("div", { className: classes.actions },
            React.createElement("button", { className: classes.button, disabled: !canExport, onClick: copyTranscript, type: 'button' }, exportStatus === 'success' ? t('captionTranscript.copied') : exportStatus === 'pending' ? t('captionTranscript.copying') : t('captionTranscript.copy'))),
        (exportStatus === 'error' || exportStatus === 'unavailable') && (React.createElement("div", { className: classes.exportError, role: 'alert' }, exportStatus === 'error'
            ? t('captionTranscript.copyError')
            : t('captionTranscript.unavailable'))),
        React.createElement("div", { className: classes.privacyNote }, t('captionTranscript.privacyCopy')),
        React.createElement("div", { className: classes.transcript }, !preferences.keepTranscript ? (React.createElement("div", { className: classes.empty }, transcriptRetentionAllowed
            ? t('captionTranscript.off')
            : t('captionSettings.restricted'))) : entries.length ? entries.map(entry => {
            const label = toneLabel(entry.captionTone);
            return (React.createElement("div", { className: classes.entry, key: entry.id },
                React.createElement("div", { className: classes.meta },
                    React.createElement("span", null, formatTime(entry.timestamp)),
                    preferences.showSpeakerNames && (React.createElement("strong", null,
                        React.createElement("bdi", null, entry.participantName || t('captionTranscript.unknownSpeaker')))),
                    !entry.final && React.createElement("span", null, t('captionTranscript.interim')),
                    label && React.createElement("span", { "aria-hidden": !preferences.screenReaderToneHints }, label)),
                React.createElement("div", { className: classes.text, dir: 'auto' }, entry.text)));
        }) : (React.createElement("div", { className: classes.empty }, t('captionTranscript.empty'))))));
};
export default CaptionTranscriptDialog;
